import type {
  ProjectTenderResponse,
  TenderOperationRates,
  TenderOperationShares,
  TenderReservedHours,
} from './schemas/project-organization';
import { tenderOperationCodes } from './schemas/project-organization';

/**
 * Tender money math, shared by the API service and the web page.
 *
 * The web page recomputes these figures locally so an edit moves everything it
 * feeds on the keystroke instead of on the round-trip. That only works if the
 * client reproduces the server's arithmetic *exactly* — a one-cent divergence
 * would be deterministic per project, so the figure would visibly twitch every
 * time a response landed, and near zero it could flash the negative-labor
 * banner on and off.
 *
 * float64 cannot give that guarantee. Fuzzing `Math.round(base * pct / 100 *
 * 100) / 100` against `Prisma.Decimal`'s `base.mul(pct).div(100)
 * .toDecimalPlaces(2)` over 300k random pairs produced 19 one-cent mismatches
 * (e.g. 15393.00 @ 66.50% → 10236.35 server vs 10236.34 float), and the
 * cost/rate division diverged 25 times. The integer-cent arithmetic below
 * produced zero mismatches over the same inputs, and does so by construction
 * rather than by luck: Prisma.Decimal rounds ROUND_HALF_UP (half away from
 * zero), which `divHalfUp` reproduces exactly.
 *
 * PRECONDITION: every money/percent input is stored at scale 2 —
 * Project.totalNet and ProjectTender.manualMaterialsCost are Decimal(12,2), the
 * six percent columns Decimal(5,2), TenderSetting rates Decimal(8,2), and the
 * BOM grand total arrives pre-formatted to 2dp. Converting those to integer
 * cents is lossless. If any of those scales ever grows, this module truncates
 * silently — the parity spec in apps/api guards that.
 */

/** Exact cents from a 2dp decimal string or a number carrying at most 2dp. */
function toCents(value: string | number): bigint {
  const text = typeof value === 'number' ? value.toFixed(2) : value.trim();
  const negative = text.startsWith('-');
  const digits = negative ? text.slice(1) : text;
  const [whole, fraction = ''] = digits.split('.');
  // Pad/truncate to exactly two fractional digits; inputs are 2dp by contract,
  // so truncation here can only ever drop zeroes.
  const cents = BigInt(whole || '0') * 100n + BigInt((fraction + '00').slice(0, 2));
  return negative ? -cents : cents;
}

/** Inverse of toCents: a sign-correct 2dp decimal string, never "-0.00". */
function fromCents(cents: bigint): string {
  const negative = cents < 0n;
  const absolute = negative ? -cents : cents;
  const whole = absolute / 100n;
  const fraction = absolute % 100n;
  const text = `${whole}.${fraction.toString().padStart(2, '0')}`;
  return negative && absolute !== 0n ? `-${text}` : text;
}

/** Divide rounding half away from zero — Prisma.Decimal's ROUND_HALF_UP. */
function divHalfUp(numerator: bigint, denominator: bigint): bigint {
  if (denominator === 0n) throw new Error('divHalfUp: division by zero');
  const negative = numerator < 0n !== denominator < 0n;
  const n = numerator < 0n ? -numerator : numerator;
  const d = denominator < 0n ? -denominator : denominator;
  // (2n + d) / 2d rounds halves away from zero on the magnitude, then the sign
  // is reapplied — so -0.005 goes to -0.01, matching the server.
  const magnitude = (2n * n + d) / (2n * d);
  return negative ? -magnitude : magnitude;
}

export interface TenderDerivationInput {
  /** Project currency; the hour figures are only derived for RON. */
  currency: string;
  /** Project.totalNet — the base every percentage is taken from. */
  totalOfferNet: string;
  /** BOM grand total across all rooms. */
  materialsCost: string;
  /** Share-weighted mean of the operation rates, RON/h at 2dp. */
  averageHourlyRate: number;
  profitPct: number;
  commissionPct: number;
  additionalPct: number;
  /** null means "no manual scenario entered". */
  manualMaterialsCost: string | null;
  manualProfitPct: number;
  manualCommissionPct: number;
  manualAdditionalPct: number;
}

/**
 * Exactly the derived subset of the response. Typed off ProjectTenderResponse
 * on purpose: adding a derived field to the contract fails to compile until it
 * is derived here, so the two cannot drift.
 */
export type TenderDerived = Pick<
  ProjectTenderResponse,
  | 'profitAmount'
  | 'commissionAmount'
  | 'additionalAmount'
  | 'laborCost'
  | 'laborHours'
  | 'manualProfitAmount'
  | 'manualCommissionAmount'
  | 'manualAdditionalAmount'
  | 'manualLaborCost'
  | 'manualLaborHours'
  | 'materialsCostDifference'
>;

/**
 * Hourly rates averaged by the time each operation actually takes — the "Tarif
 * mediu" (RON/h, 2dp): `Σ(rate × share) / Σ(share)`.
 *
 * Weighted rather than a straight mean because this figure is the money→hours
 * divisor for the labour residual (see `hoursFor`), and a budget is spent in
 * proportion to the configured split, not evenly across the price list. A shop
 * that gives Montaj 40% of its hours converts money to hours mostly at the
 * Montaj rate; a plain mean would let an operation that takes almost no time
 * drag the whole conversion. The same value drives the display and the divisor,
 * so the two cannot disagree.
 *
 * With every share at 0 there is no split to weight by. Rather than fail — or
 * report 0 and silently strip every project of an hour budget — it falls back
 * to the straight mean over all operations. The page already warns loudly that
 * the shares do not total 100, which is where that state is visible.
 */
export function tenderAverageHourlyRate(
  rates: TenderOperationRates,
  shares: TenderOperationShares,
): number {
  // Both operands are exact in hundredths, so the products and their sums are
  // exact and the single divHalfUp below is the only rounding — matching the
  // server's Decimal ROUND_HALF_UP, as everywhere else in this module.
  let weighted = 0n;
  let totalShare = 0n;
  for (const code of tenderOperationCodes) {
    const share = toCents(shares[code]);
    weighted += toCents(rates[code]) * share;
    totalShare += share;
  }
  if (totalShare === 0n) {
    const total = tenderOperationCodes.reduce((sum, code) => sum + toCents(rates[code]), 0n);
    return Number(fromCents(divHalfUp(total, BigInt(tenderOperationCodes.length))));
  }
  return Number(fromCents(divHalfUp(weighted, totalShare)));
}

/**
 * Split a labour-hour budget across the operations by their configured shares.
 *
 * Each operation gets `hours × share%`, rounded to two decimals the same
 * half-up way as every other figure here. The parts are NOT forced to re-sum to
 * the budget: shares that do not total 100 are a real state the page warns
 * about, and quietly absorbing the difference into one operation would hide it.
 */
export function distributeHoursByShare(
  laborHours: number,
  shares: TenderOperationShares,
): TenderReservedHours {
  const budget = toCents(laborHours);
  const split = (share: number): number =>
    Number(fromCents(divHalfUp(budget * toCents(share), 10_000n)));
  // Built from the code tuple rather than a hand-written literal so adding an
  // operation never needs an edit here — a missing key would otherwise be an
  // undefined that silently reads as 0 hours.
  return Object.fromEntries(
    tenderOperationCodes.map((code) => [code, split(shares[code])]),
  ) as TenderReservedHours;
}

/**
 * Recomputes the whole derived block from raw inputs. Whole-block rather than
 * incremental: patching individual figures would mean hand-encoding the
 * dependency graph, and re-rounding already-rounded amounts accumulates error
 * across successive edits.
 */
export function deriveTender(input: TenderDerivationInput): TenderDerived {
  const baseCents = toCents(input.totalOfferNet);
  const rateCents = toCents(input.averageHourlyRate);

  /** pct is Decimal(5,2), so pct-hundredths over 10_000 keeps it exact. */
  const pctOfBase = (pct: number): bigint => divHalfUp(baseCents * toCents(pct), 10_000n);

  // Hours are only derived for RON: the operation rates are RON/h, so dividing
  // a labor budget in another currency by them would silently mix currencies.
  const hoursFor = (costCents: bigint): number | null => {
    if (rateCents === 0n || input.currency !== 'RON') return null;
    return Number(fromCents(divHalfUp(costCents * 100n, rateCents)));
  };

  const profit = pctOfBase(input.profitPct);
  const commission = pctOfBase(input.commissionPct);
  const additional = pctOfBase(input.additionalPct);
  // Every operand is already in cents, so this needs no rounding at all.
  const labor = baseCents - toCents(input.materialsCost) - profit - commission - additional;

  const manualProfit = pctOfBase(input.manualProfitPct);
  const manualCommission = pctOfBase(input.manualCommissionPct);
  const manualAdditional = pctOfBase(input.manualAdditionalPct);

  // No manual material cost means no manual scenario at all: the derived
  // figures stay null rather than defaulting materials to 0, which would read
  // as a real (and wildly optimistic) estimate. null must never become 0 here.
  const manualMaterials =
    input.manualMaterialsCost === null ? null : toCents(input.manualMaterialsCost);
  const manualLabor =
    manualMaterials === null
      ? null
      : baseCents - manualMaterials - manualProfit - manualCommission - manualAdditional;

  return {
    profitAmount: fromCents(profit),
    commissionAmount: fromCents(commission),
    additionalAmount: fromCents(additional),
    laborCost: fromCents(labor),
    laborHours: hoursFor(labor),
    manualProfitAmount: fromCents(manualProfit),
    manualCommissionAmount: fromCents(manualCommission),
    manualAdditionalAmount: fromCents(manualAdditional),
    manualLaborCost: manualLabor === null ? null : fromCents(manualLabor),
    manualLaborHours: manualLabor === null ? null : hoursFor(manualLabor),
    materialsCostDifference:
      manualMaterials === null ? null : fromCents(toCents(input.materialsCost) - manualMaterials),
  };
}
